import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, RefreshCw, RotateCcw, X } from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
  Button,
  Card,
  StatusBadge,
  type StatusTone,
  NotificationCard,
  useToast,
  PeriodFilter,
  SearchField,
  TABLE_PRESETS,
} from '@sighthost/ui';
import {
  provisioningService,
  type ProvisioningJobRow,
  type ProvisioningStatus,
  type ProvisioningJobDetail,
} from '@/domains/ops/services/provisioningService';

const STATUS_OPTIONS: { value: ProvisioningStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All non-complete' },
  { value: 'pending', label: 'Pending' },
  { value: 'running', label: 'Running' },
  { value: 'failed_retryable', label: 'Failed (retryable)' },
  { value: 'failed_terminal', label: 'Failed (terminal)' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_TONE: Record<ProvisioningStatus, StatusTone> = {
  pending: 'pending',
  running: 'info',
  failed_retryable: 'pending',
  failed_terminal: 'failed',
  cancelled: 'draft',
};

const POLL_MS = 30_000;

export default function ProvisioningQueuePage() {
  const [rows, setRows] = useState<ProvisioningJobRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [statusFilter, setStatusFilter] = useState<ProvisioningStatus | 'all'>(
    'all'
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /**
   * The queue is FLOW — jobs are events — so the period is meaningful and is
   * labelled `Created`. AR-5 option (a): 98 jobs against a 200 cap, measured
   * 2026-09-13, so the fetched set is complete.
   *
   * The status select stays where it is: it is already sent to the service and
   * drives the auto-refresh, so folding it into `facets` would change what is
   * fetched, not just what is shown.
   */
  const table = useTableFilters<ProvisioningJobRow>({
    rows,
    dateMode: 'client',
    dateLabel: 'Created',
    dateOf: (r) => r.created_at,
    searchText: (r) =>
      [
        r.step_kind,
        r.target_ref ?? '',
        r.customer_name ?? '',
        r.customer_email ?? '',
        r.error_code ?? '',
      ].join(' '),
  });
  const [selectedDetail, setSelectedDetail] =
    useState<ProvisioningJobDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const { toast } = useToast();

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      try {
        const data = await provisioningService.listQueue({
          status: statusFilter,
        });
        setRows(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [statusFilter]
  );

  // Slice 1 (2026-09-10): retry = a NEW job via the EF, never a status reset
  // (AR-S7 — BEFORE INSERT guards must re-fire). A guard refusal is shown as
  // the outcome; the original row stays as it was.
  const retryJob = useCallback(
    async (jobId: string) => {
      setRetrying(true);
      try {
        const res = await provisioningService.retry(jobId);
        toast({
          tone: 'success',
          title: 'Retry queued',
          body: `New job ${res.new_job_id.slice(0, 8)}… for ${res.step_kind} on ${res.target_ref ?? '—'}. The runner picks it up within a minute.`,
        });
        setSelectedId(res.new_job_id);
        await load(true);
      } catch (err) {
        toast({
          tone: 'warning',
          title: 'Retry not queued',
          body: err instanceof Error ? err.message : String(err),
        });
      } finally {
        setRetrying(false);
      }
    },
    [load, toast]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh every 30 s
  useEffect(() => {
    const t = window.setInterval(() => {
      load(true);
    }, POLL_MS);
    return () => window.clearInterval(t);
  }, [load]);

  // Lazy-load detail on row click
  useEffect(() => {
    if (!selectedId) {
      setSelectedDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    provisioningService
      .getById(selectedId)
      .then((d) => {
        if (!cancelled) {
          setSelectedDetail(d);
          setDetailLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const columns = useMemo<UniversalTableColumn<ProvisioningJobRow>[]>(
    () => [
      {
        key: 'id',
        header: 'Job',
        render: (r) => (
          <code className="text-xs">{r.id.slice(0, 8)}…</code>
        ),
        width: 'w-28',
      },
      {
        key: 'step',
        header: 'Step',
        sortValue: (r) => r.step_kind,
        render: (r) => (
          <span className="text-xs">{r.step_kind}</span>
        ),
      },
      {
        key: 'target',
        header: 'Target',
        sortValue: (r) => r.target_ref ?? '',
        render: (r) =>
          r.target_ref ? (
            <code className="text-xs">{r.target_ref}</code>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        key: 'customer',
        header: 'Customer',
        sortValue: (r) => r.customer_name ?? '',
        render: (r) => (
          <div className="flex flex-col">
            <span>{r.customer_name ?? '—'}</span>
            {r.customer_email && (
              <span className="text-xs text-muted-foreground">
                {r.customer_email}
              </span>
            )}
          </div>
        ),
        hideOnMobile: true,
      },
      {
        key: 'status',
        header: 'Status',
        sortValue: (r) => r.status,
        render: (r) => (
          <StatusBadge tone={STATUS_TONE[r.status]} dot>
            {r.status.replace('_', ' ')}
          </StatusBadge>
        ),
      },
      {
        key: 'priority',
        header: 'Pri',
        align: 'right',
        sortValue: (r) => r.priority,
        render: (r) => <span className="tabular-nums">{r.priority}</span>,
        hideOnMobile: true,
      },
      {
        key: 'attempts',
        header: 'Attempts',
        align: 'right',
        sortValue: (r) => r.attempt_count,
        render: (r) => (
          <span className="tabular-nums text-muted-foreground">
            {r.attempt_count} / {r.max_attempts}
          </span>
        ),
        hideOnMobile: true,
      },
      {
        key: 'error',
        header: 'Error',
        render: (r) =>
          r.error_message ? (
            <span
              className="block max-w-xs truncate text-xs text-danger"
              title={r.error_message}
            >
              {r.error_message}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
        hideOnMobile: true,
      },
    ],
    []
  );

  return (
    <UniversalPage
      title="Provisioning queue"
      description="Live view of pending, running, and failed provisioning jobs."
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Ops' },
        { label: 'Provisioning' },
      ]}
      actions={
        <Button
          variant="outline"
          size="sm"
          onClick={() => load()}
          disabled={refreshing}
        >
          <RefreshCw className={refreshing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          Refresh
        </Button>
      }
    >
      <Card interactive={false} className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor="status" className="text-sm text-muted-foreground">
            Status
          </label>
          <select
            id="status"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as ProvisioningStatus | 'all')
            }
            className="h-9 rounded border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <PeriodFilter
            value={table.filters.period}
            from={table.filters.from}
            to={table.filters.to}
            onChange={table.filters.setPeriod}
            onStep={table.stepPeriodBy}
            presets={TABLE_PRESETS}
            variant="menu"
            steppable
            label="Created"
          />
          <SearchField
            value={table.q}
            onValueChange={table.setQ}
            pending={table.isSearching}
            placeholder="Search step, target, customer, error"
            className="w-full max-w-xs"
          />
          <span className="ml-auto text-xs text-muted-foreground">
            {loading ? 'Loading…' : 'auto-refresh 30s'}
          </span>
        </div>
      </Card>

      {error && !loading && (
        <NotificationCard
          tone="danger"
          title="Could not load queue"
          body={error.message}
        />
      )}

      <UniversalTable<ProvisioningJobRow>
        data={table.rows}
        columns={columns}
        getRowKey={(r) => r.id}
        onRowClick={(r) => setSelectedId(r.id)}
        isLoading={loading}
        caption={loading ? undefined : table.caption}
        empty={{
          icon: Activity,
          title: 'Queue is empty',
          description: 'No pending, running, or failed jobs match the filter.',
        }}
      />

      {selectedId && (
        <JobDrawer
          jobId={selectedId}
          job={selectedDetail}
          row={rows.find((r) => r.id === selectedId) ?? null}
          loading={detailLoading}
          retrying={retrying}
          onRetry={() => retryJob(selectedId)}
          onClose={() => setSelectedId(null)}
        />
      )}
    </UniversalPage>
  );
}

// Mirrors provisioning-job-retry (2026-09-30, AR-OI-7) — change both together.
// failed_retryable is never offered: the runner still owns that job.
// Account creation retries only from failed_terminal.
const RETRYABLE_STATUSES: ProvisioningStatus[] = ['failed_terminal', 'cancelled'];
const CREATEACCT_RETRYABLE_STATUSES: ProvisioningStatus[] = ['failed_terminal'];

function JobDrawer({
  jobId,
  job,
  row,
  loading,
  retrying,
  onRetry,
  onClose,
}: {
  jobId: string;
  job: ProvisioningJobDetail | null;
  row: ProvisioningJobRow | null;
  loading: boolean;
  retrying: boolean;
  onRetry: () => void;
  onClose: () => void;
}) {
  const status = job?.status ?? row?.status ?? null;
  const errorCode = job?.error_code ?? row?.error_code ?? null;
  const alreadyRetried = !!errorCode && errorCode.startsWith('retried_as:');
  const isCreate = (job?.step_kind ?? row?.step_kind) === 'whm.createacct';
  const canRetry =
    !!status &&
    (isCreate ? CREATEACCT_RETRYABLE_STATUSES : RETRYABLE_STATUSES).includes(status) &&
    !alreadyRetried;
  const irreversible =
    (job?.step_kind ?? row?.step_kind) === 'whm.removeacct' ||
    (job?.step_kind ?? row?.step_kind) === 'whm.scheduled_terminate';
  return (
    <div
      className="fixed inset-0 z-30 flex"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="absolute inset-0 bg-scrim/40 backdrop-blur-sm"
        aria-hidden
      />
      <aside
        className="relative ml-auto flex h-full w-full max-w-2xl flex-col bg-card shadow-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Provisioning job detail"
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-6 py-4">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">{jobId}</p>
            <h3 className="mt-0.5 text-sm font-semibold text-foreground">
              {row?.step_kind ?? job?.step_kind ?? '—'}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              target={row?.target_ref ?? '—'} · attempts={row?.attempt_count ?? '?'}/
              {row?.max_attempts ?? '?'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canRetry && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRetry}
                disabled={retrying || loading}
                title={
                  irreversible
                    ? 'Irreversible step — admin or super_admin only; every database guard re-fires on the new job'
                    : 'Queues a NEW job with the same target and payload; the original is marked retried'
                }
              >
                <RotateCcw className={retrying ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
                {retrying ? 'Queuing…' : 'Retry job'}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onClose} aria-label="Close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading || !job ? (
            <p className="text-sm text-muted-foreground">Loading payload…</p>
          ) : (
            <>
              <Field label="Status" value={job.status} />
              <Field label="Priority" value={String(job.priority)} />
              <Field
                label="Scheduled for"
                value={new Date(job.scheduled_for).toLocaleString('en-ZA')}
              />
              {job.started_at && (
                <Field
                  label="Started at"
                  value={new Date(job.started_at).toLocaleString('en-ZA')}
                />
              )}
              {job.error_message && (
                <NotificationCard
                  tone="danger"
                  title={job.error_code ?? 'Error'}
                  body={job.error_message}
                />
              )}
              <JsonBlock label="Payload" value={job.payload} />
              <JsonBlock
                label="Last response"
                value={job.last_response_payload}
              />
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="text-sm text-foreground">{value}</p>
    </div>
  );
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  const text = value === null || value === undefined ? '—' : safeStringify(value);
  return (
    <div>
      <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <pre className="max-h-[40vh] overflow-auto rounded border border-border bg-muted p-3 text-xs leading-relaxed text-foreground">
        {text}
      </pre>
    </div>
  );
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}
